import { InjectionKey, Ref, inject, ref } from 'vue'

export type Layout = 'content-only' | 'app-bar' | 'panel'

export interface LayoutOptions {
	defaultLayout?: Layout
}
export interface LayoutInstance {
	name: Ref<Layout>
	setLayout: (_layout?: Nullable<Layout>) => void
	title: Ref<Nullable<string>>
	setTitle: (_title: Nullable<string>) => void
	hideBackBtn: Ref<boolean>
	setHideBackBtn: (_hideBackBtn?: boolean) => void
	onBackClick: Ref<Nullable<() => void>>
	setOnBackClick: (_onBackClick: Nullable<() => void>) => void
	endIcon: Ref<Nullable<string>>
	setEndIcon: (_endIcon?: Nullable<string>) => void
	endIconSize: Ref<Nullable<number | string>>
	setEndIconSize: (_endIconSize?: Nullable<number | string>) => void
	onEndIconClick: Ref<Nullable<() => void>>
	setOnEndIconClick: (_onEndIconClick?: Nullable<() => void>) => void
}

// types for meta
export interface MetaObjectLayout {
	name?: Layout
	title?: Nullable<string>
	hideBackBtn?: boolean
	endIcon?: Nullable<string>
	endIconSize?: Nullable<number | string>
}
export type MetaLayout = MetaObjectLayout | Layout

export const LayoutSymbol: InjectionKey<LayoutInstance> =
	Symbol.for('ui-kit:layout')

export function createLayout(options: LayoutOptions = {}) {
	const name = ref<Layout>(options.defaultLayout || 'content-only')
	const setLayout = (_layout?: Nullable<Layout>) => {
		if (_layout) {
			name.value = _layout
			return
		}

		name.value = options.defaultLayout || 'content-only'
	}

	const title = ref<Nullable<string>>(null)
	const setTitle = (_title: Nullable<string>) => (title.value = _title)

	const hideBackBtn = ref(false)
	const setHideBackBtn = (_hideBackBtn?: boolean) =>
		(hideBackBtn.value = _hideBackBtn === undefined ? false : _hideBackBtn)

	const onBackClick = ref<Nullable<() => void>>(null)
	const setOnBackClick = (_onBackClick: Nullable<() => void>) =>
		(onBackClick.value = _onBackClick)

	const endIcon = ref<Nullable<string>>(null)
	const setEndIcon = (_endIcon?: Nullable<string>) =>
		(endIcon.value = _endIcon || null)

	const endIconSize = ref<Nullable<number | string>>(null)
	const setEndIconSize = (_endIconSize?: Nullable<number | string>) =>
		(endIconSize.value = _endIconSize || null)

	const onEndIconClick = ref<Nullable<() => void>>(null)
	const setOnEndIconClick = (_onEndIconClick?: Nullable<() => void>) =>
		(onEndIconClick.value = _onEndIconClick || null)

	return {
		name,
		setLayout,
		title,
		setTitle,
		hideBackBtn,
		setHideBackBtn,
		onBackClick,
		setOnBackClick,
		endIcon,
		setEndIcon,
		endIconSize,
		setEndIconSize,
		onEndIconClick,
		setOnEndIconClick
	} as LayoutInstance
}

export function useLayout() {
	const layout = inject(LayoutSymbol)

	if (!layout) {
		throw new Error('[UiKit] Could not find layout instance')
	}

	return layout
}
